import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Ticket, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";

const DiscountCodesAdmin = () => {
  const { token } = useAuth();
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, total_uses: 0 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    discount_percent: 10,
    description: "",
    max_uses: null
  });

  const discountOptions = [
    { value: 10, label: "10% Off" },
    { value: 20, label: "20% Off" },
    { value: 50, label: "50% Off" },
    { value: 90, label: "90% Off" },
    { value: 100, label: "100% Off (Free)" }
  ];

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/discount-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(response.data.codes || []);
      setStats({
        total: response.data.total,
        active: response.data.active,
        total_uses: response.data.total_uses
      });
    } catch (error) {
      console.error("Failed to fetch discount codes:", error);
      toast.error("Failed to load discount codes");
    } finally {
      setLoading(false);
    }
  };

  const generateRandomCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, code });
  };

  const handleSave = async () => {
    if (!formData.code || formData.code.length !== 8) {
      toast.error("Code must be exactly 8 characters");
      return;
    }

    try {
      if (editingCode) {
        await axios.put(`${API}/admin/discount-codes/${editingCode.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Discount code updated");
      } else {
        await axios.post(`${API}/admin/discount-codes`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Discount code created");
      }
      setDialogOpen(false);
      setEditingCode(null);
      setFormData({ code: "", discount_percent: 10, description: "", max_uses: null });
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save discount code");
    }
  };

  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      discount_percent: code.discount_percent,
      description: code.description || "",
      max_uses: code.max_uses
    });
    setDialogOpen(true);
  };

  const handleDelete = async (codeId) => {
    if (!confirm("Delete this discount code?")) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Discount code deleted");
      fetchCodes();
    } catch (error) {
      toast.error("Failed to delete discount code");
    }
  };

  const handleToggleActive = async (codeId) => {
    try {
      await axios.post(`${API}/admin/discount-codes/${codeId}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCodes();
    } catch (error) {
      toast.error("Failed to toggle code status");
    }
  };

  const calculateDiscountedPrice = (percent) => {
    const basePrice = 2.50;
    return (basePrice - (basePrice * percent / 100)).toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-discount-codes">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <Ticket className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          DISCOUNT CODES
        </h1>
        <Button 
          onClick={() => {
            setEditingCode(null);
            setFormData({ code: "", discount_percent: 10, description: "", max_uses: null });
            setDialogOpen(true);
          }} 
          className="bg-[#7C2C3B] hover:bg-[#63232F]"
        >
          <Plus className="w-4 h-4 mr-2" /> CREATE CODE
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-4 text-center">
          <div className="text-3xl font-bold text-[#0F172A]">{stats.total}</div>
          <div className="text-sm text-slate-500">Total Codes</div>
        </div>
        <div className="bg-green-50 rounded-lg border border-green-200 p-4 text-center">
          <div className="text-3xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-green-600">Active</div>
        </div>
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4 text-center">
          <div className="text-3xl font-bold text-blue-600">{stats.total_uses}</div>
          <div className="text-sm text-blue-600">Total Uses</div>
        </div>
        <div className="bg-[#7C2C3B]/10 rounded-lg border border-[#7C2C3B]/30 p-4 text-center">
          <div className="text-3xl font-bold text-[#7C2C3B]">£2.50</div>
          <div className="text-sm text-[#7C2C3B]">Base Price</div>
        </div>
      </div>

      {/* Codes Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Final Price</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Uses</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {codes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                  <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No discount codes yet. Create your first code!</p>
                </TableCell>
              </TableRow>
            ) : (
              codes.map(code => (
                <TableRow key={code.id}>
                  <TableCell>
                    <span className="font-mono font-bold text-lg bg-slate-100 px-3 py-1 rounded">
                      {code.code}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                      code.discount_percent === 100 ? "bg-green-100 text-green-700" :
                      code.discount_percent >= 50 ? "bg-amber-100 text-amber-700" :
                      "bg-blue-100 text-blue-700"
                    }`}>
                      {code.discount_percent}% OFF
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="font-bold text-[#7C2C3B]">
                      £{calculateDiscountedPrice(code.discount_percent)}
                    </span>
                    {code.discount_percent === 100 && (
                      <span className="ml-2 text-xs text-green-600 font-medium">FREE</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-600">{code.description || "-"}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium">
                      {code.times_used || 0}
                      {code.max_uses && <span className="text-slate-400">/{code.max_uses}</span>}
                    </span>
                  </TableCell>
                  <TableCell>
                    <button
                      onClick={() => handleToggleActive(code.id)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                        code.is_active
                          ? "bg-green-100 text-green-700 hover:bg-green-200"
                          : "bg-red-100 text-red-700 hover:bg-red-200"
                      }`}
                    >
                      {code.is_active ? "Active" : "Inactive"}
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      <Button
                        onClick={() => handleEdit(code)}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(code.id)}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
              {editingCode ? "EDIT DISCOUNT CODE" : "CREATE DISCOUNT CODE"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Code (8 characters)</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().slice(0, 8) })}
                  placeholder="e.g., WHUFC2024"
                  maxLength={8}
                  className="font-mono text-lg tracking-wider"
                  data-testid="discount-code-input"
                />
                <Button type="button" variant="outline" onClick={generateRandomCode}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                {formData.code.length}/8 characters (letters and numbers only)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Discount Amount</Label>
              <Select
                value={formData.discount_percent.toString()}
                onValueChange={(val) => setFormData({ ...formData, discount_percent: parseInt(val) })}
              >
                <SelectTrigger data-testid="discount-percent-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {discountOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>
                      {opt.label} - Final price: £{calculateDiscountedPrice(opt.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Launch promotion"
                data-testid="discount-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Max Uses (optional)</Label>
              <Input
                type="number"
                value={formData.max_uses || ""}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="Leave empty for unlimited"
                min="1"
                data-testid="discount-max-uses-input"
              />
            </div>

            {/* Preview Card */}
            <div className="bg-gradient-to-r from-[#7C2C3B] to-[#5a1f2b] rounded-lg p-4 text-white">
              <div className="text-sm text-white/70 mb-1">Preview</div>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-mono text-2xl font-bold">{formData.code || "????????"}</span>
                  <div className="text-white/80 text-sm mt-1">{formData.discount_percent}% discount</div>
                </div>
                <div className="text-right">
                  <div className="text-sm line-through text-white/50">£2.50</div>
                  <div className="text-2xl font-bold">£{calculateDiscountedPrice(formData.discount_percent)}</div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSave}
                className="bg-[#7C2C3B] hover:bg-[#63232F]"
                disabled={formData.code.length !== 8}
                data-testid="save-discount-code-btn"
              >
                {editingCode ? "Update Code" : "Create Code"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscountCodesAdmin;
